import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { motion, useInView } from 'framer-motion';
import '../../styles/sections/mediagallery.css';

// ─────────────────────────────────────────────
// Asset imports – all 16 images from mediagallery
// ─────────────────────────────────────────────
import media1  from '../../assets/mediagallery/media1.jpeg';
import media2  from '../../assets/mediagallery/media2.jpeg';
import media3  from '../../assets/mediagallery/media3.jpeg';
import media4  from '../../assets/mediagallery/media4.jpeg';
import media5  from '../../assets/mediagallery/media5.jpeg';
import media6  from '../../assets/mediagallery/media6.jpeg';
import media7  from '../../assets/mediagallery/media7.jpeg';
import media8  from '../../assets/mediagallery/media8.jpeg';
import media9  from '../../assets/mediagallery/media9.jpeg';
import media10 from '../../assets/mediagallery/media10.jpeg';
import media11 from '../../assets/mediagallery/media11.jpeg';
import media12 from '../../assets/mediagallery/media12.jpeg';
import media13 from '../../assets/mediagallery/media13.jpeg';
import media14 from '../../assets/mediagallery/media14.jpeg';
import media15 from '../../assets/mediagallery/media15.jpeg';
import media16 from '../../assets/mediagallery/media16.jpeg';

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────
const GALLERY_ITEMS = [
  { id: '1',  img: media1,  height: 520 },
  { id: '2',  img: media2,  height: 380 },
  { id: '3',  img: media3,  height: 460 },
  { id: '4',  img: media4,  height: 340 },
  { id: '5',  img: media5,  height: 500 },
  { id: '6',  img: media6,  height: 420 },
  { id: '7',  img: media7,  height: 360 },
  { id: '8',  img: media8,  height: 480 },
  { id: '9',  img: media9,  height: 440 },
  { id: '10', img: media10, height: 390 },
  { id: '11', img: media11, height: 530 },
  { id: '12', img: media12, height: 370 },
  { id: '13', img: media13, height: 490 },
  { id: '14', img: media14, height: 350 },
  { id: '15', img: media15, height: 510 },
  { id: '16', img: media16, height: 400 },
];

const MEDIA_LINKS = [
  {
    id: 1,
    title: 'Aashman Technicals — Video 1',
    description: 'Watch our latest robotics and technology showcase.',
    channel: 'Aashman Technicals',
    url: 'https://youtu.be/p-oix915gSY?si=gmsJ3bLUTBKIFzL-',
  },
  {
    id: 2,
    title: 'Aashman Technicals — Video 2',
    description: 'Explore our innovations in action.',
    channel: 'Aashman Technicals',
    url: 'https://youtu.be/LT38kImvaes?si=kv3pHBu6va6uY3cV',
  },
  {
    id: 3,
    title: 'Aashman Technicals — Video 3',
    description: 'See our engineering solutions at work.',
    channel: 'Aashman Technicals',
    url: 'https://youtu.be/dvvzjeOxb4A?si=5RLYW_GSPv1ghorv',
  },
  {
    id: 4,
    title: 'Aashman Technicals — Video 4',
    description: 'A closer look at our product demonstrations.',
    channel: 'Aashman Technicals',
    url: 'https://youtu.be/DizkN9XQ8Lk?si=bfUxMmAG6WvLTauv',
  },
  {
    id: 5,
    title: 'Aashman Technicals — Video 5',
    description: 'Real-world performance and field testing.',
    channel: 'Aashman Technicals',
    url: 'https://youtu.be/67OZ8KCXXU4?si=p23ebNc8qp-y07Ol',
  },
];

// ─────────────────────────────────────────────
// HOOKS
// ─────────────────────────────────────────────
const useMedia = (queries, values, defaultValue) => {
  const get = useCallback(
    () => values[queries.findIndex(q => matchMedia(q).matches)] ?? defaultValue,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queries, values, defaultValue]
  );
  const [value, setValue] = useState(get);
  useEffect(() => {
    const handler = () => setValue(get);
    queries.forEach(q => matchMedia(q).addEventListener('change', handler));
    return () => queries.forEach(q => matchMedia(q).removeEventListener('change', handler));
  }, [queries, get]);
  return value;
};

const useMeasure = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
};

// ─────────────────────────────────────────────
// ZOOM CONSTANTS
// ─────────────────────────────────────────────
const MIN_SCALE = 1;
const MAX_SCALE = 1.8;           // hard cap: 80% increase only
const ZOOM_STEP = 0.12;          // per wheel tick (normalized)
const DOUBLE_TAP_MS = 280;       // max ms between two taps

// ─────────────────────────────────────────────
// LIGHTBOX — with advanced zoom/pan/pinch
// ─────────────────────────────────────────────
const Lightbox = ({ item, onClose, onPrev, onNext }) => {
  // ── Refs — all zoom/pan state lives here to avoid re-renders ──
  const imgRef        = useRef(null);   // the mg-lightbox-content element — entire frame (border+shadow+image) zooms
  const rawImgRef     = useRef(null);   // the actual <img> element (offsetWidth/offsetHeight for clamp math only)
  const backdropRef   = useRef(null);   // backdrop div — touch/wheel listeners attach here to capture full-screen gestures
  const navPrevRef    = useRef(null);   // ‹ button outside zooming frame — hidden on mobile when zoomed
  const navNextRef    = useRef(null);   // › button outside zooming frame — hidden on mobile when zoomed
  const navCallbackRef = useRef({ prev: null, next: null }); // stable ref to latest nav callbacks
  const stateRef      = useRef({        // mutable zoom/pan state
    scale:   1,
    tx:      0,
    ty:      0,
    // drag tracking
    dragging:    false,
    dragStartX:  0,
    dragStartY:  0,
    txAtDrag:    0,
    tyAtDrag:    0,
    // pinch tracking
    pinching:      false,
    pinchDist0:    0,
    scaleAtPinch:  0,
    pinchMidX:     0,
    pinchMidY:     0,
    txAtPinch:     0,
    tyAtPinch:     0,
    // double-tap
    lastTapTime:   0,
    // swipe-to-navigate (scale===1 only)
    swipeStartX:   0,
    swipeStartY:   0,
    swipeTracking: false,
  });

  // ── Show/hide nav buttons on mobile when zoomed (direct DOM, no re-render) ──
  const setNavVisibility = useCallback((scale) => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const visible = !isMobile || scale <= 1;
    const opacity = visible ? '' : '0';
    const pointerEvents = visible ? '' : 'none';
    if (navPrevRef.current) {
      navPrevRef.current.style.opacity = opacity;
      navPrevRef.current.style.pointerEvents = pointerEvents;
    }
    if (navNextRef.current) {
      navNextRef.current.style.opacity = opacity;
      navNextRef.current.style.pointerEvents = pointerEvents;
    }
  }, []);

  // ── Apply transform directly on the DOM node — zero React overhead ──
  const applyTransform = useCallback((scale, tx, ty, animated = false) => {
    const el = imgRef.current;
    if (!el) return;
    if (animated) {
      el.style.transition = 'transform 0.35s cubic-bezier(0.22,1,0.36,1)';
    } else {
      el.style.transition = 'none';
    }
    el.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    setNavVisibility(scale);
  }, [setNavVisibility]);

  // ── Compute pan boundaries so the whole content frame stays partially visible ──
  const clampPan = useCallback((scale, tx, ty) => {
    // imgRef is the mg-lightbox-content div — the element being transformed.
    // offsetWidth/offsetHeight give the unscaled rendered size (no CSS transform influence).
    const el = imgRef.current;
    if (!el) return { tx, ty };
    const renderedW = el.offsetWidth;
    const renderedH = el.offsetHeight;
    const scaledW   = renderedW * scale;
    const scaledH   = renderedH * scale;
    const maxTx = Math.max(0, (scaledW  - window.innerWidth)  / 2);
    const maxTy = Math.max(0, (scaledH  - window.innerHeight) / 2);
    return {
      tx: Math.max(-maxTx, Math.min(maxTx, tx)),
      ty: Math.max(-maxTy, Math.min(maxTy, ty)),
    };
  }, []);

  // ── Smooth animated reset ──
  const resetZoom = useCallback((animated = true) => {
    const s = stateRef.current;
    s.scale = 1;
    s.tx    = 0;
    s.ty    = 0;
    applyTransform(1, 0, 0, animated);
    // applyTransform calls setNavVisibility(1) → nav buttons shown
  }, [applyTransform]);

  // ── Keyboard / close / nav ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { resetZoom(false); onClose(); }
      if (e.key === 'ArrowLeft')  { resetZoom(false); onPrev(); }
      if (e.key === 'ArrowRight') { resetZoom(false); onNext(); }
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext, resetZoom]);

  // ── Reset zoom whenever the image changes ──
  useEffect(() => {
    resetZoom(false);
  }, [item, resetZoom]);

  // ═══════════════════════════════════════
  // WHEEL ZOOM (desktop)
  // ═══════════════════════════════════════
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      const s = stateRef.current;
      const delta = -e.deltaY * ZOOM_STEP * 0.1;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s.scale + delta * s.scale));

      // Zoom toward cursor position using screen center as transform origin
      const cx = window.innerWidth  / 2;
      const cy = window.innerHeight / 2;
      const cursorOffX = e.clientX - cx;
      const cursorOffY = e.clientY - cy;
      // Keep the world point under the cursor fixed
      const localX = (cursorOffX - s.tx) / s.scale;
      const localY = (cursorOffY - s.ty) / s.scale;
      const rawTx = cursorOffX - localX * newScale;
      const rawTy = cursorOffY - localY * newScale;

      const { tx, ty } = clampPan(newScale, rawTx, rawTy);
      s.scale = newScale;
      s.tx    = tx;
      s.ty    = ty;
      applyTransform(newScale, tx, ty, false);
    };

    // Attach wheel to backdrop so full-screen wheel events are captured
    const container = backdropRef.current || imgRef.current;
    if (!container) return;
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [applyTransform, clampPan]);

  // ═══════════════════════════════════════
  // POINTER DRAG (when zoomed > 1)
  // ═══════════════════════════════════════
  useEffect(() => {
    // Pointer drag is on the content frame (imgRef) so the full zoomed box is draggable
    const el = imgRef.current;
    if (!el) return;

    const onPointerDown = (e) => {
      if (e.pointerType === 'touch') return; // handled by touch handlers
      const s = stateRef.current;
      if (s.scale <= 1) return;
      e.preventDefault();
      s.dragging   = true;
      s.dragStartX = e.clientX;
      s.dragStartY = e.clientY;
      s.txAtDrag   = s.tx;
      s.tyAtDrag   = s.ty;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = 'grabbing';
    };

    const onPointerMove = (e) => {
      const s = stateRef.current;
      if (!s.dragging) return;
      const dx = e.clientX - s.dragStartX;
      const dy = e.clientY - s.dragStartY;
      const { tx, ty } = clampPan(s.scale, s.txAtDrag + dx, s.tyAtDrag + dy);
      s.tx = tx;
      s.ty = ty;
      applyTransform(s.scale, tx, ty, false);
    };

    const onPointerUp = () => {
      const s = stateRef.current;
      if (!s.dragging) return;
      s.dragging = false;
      el.style.cursor = s.scale > 1 ? 'grab' : 'default';
    };

    const onDoubleClick = () => {
      resetZoom(true);
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup',   onPointerUp);
    el.addEventListener('dblclick',    onDoubleClick);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup',   onPointerUp);
      el.removeEventListener('dblclick',    onDoubleClick);
    };
  }, [applyTransform, clampPan, resetZoom]);

  // ═══════════════════════════════════════
  // TOUCH: PINCH ZOOM + DRAG + DOUBLE-TAP
  // ═══════════════════════════════════════
  useEffect(() => {
    // Attach to backdrop so pinch/drag works anywhere on screen, not just over the image
    const el = backdropRef.current;
    if (!el) return;

    const getTouchDist = (t1, t2) => {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getTouchMid = (t1, t2) => ({
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    });

    const onTouchStart = (e) => {
      const s = stateRef.current;
      const touches = e.touches;

      if (touches.length === 2) {
        // Begin pinch
        e.preventDefault();
        s.pinching     = true;
        s.dragging     = false;
        s.swipeTracking = false;
        s.pinchDist0   = getTouchDist(touches[0], touches[1]);
        s.scaleAtPinch = s.scale;
        const mid      = getTouchMid(touches[0], touches[1]);
        s.pinchMidX    = mid.x;
        s.pinchMidY    = mid.y;
        s.txAtPinch    = s.tx;
        s.tyAtPinch    = s.ty;
      } else if (touches.length === 1) {
        // Double-tap detection
        const now = Date.now();
        if (now - s.lastTapTime < DOUBLE_TAP_MS) {
          e.preventDefault();
          resetZoom(true);
          s.lastTapTime = 0;
          return;
        }
        s.lastTapTime = now;

        if (s.scale > 1) {
          // Zoomed: single-finger drag
          s.dragging   = true;
          s.swipeTracking = false;
          s.pinching   = false;
          s.dragStartX = touches[0].clientX;
          s.dragStartY = touches[0].clientY;
          s.txAtDrag   = s.tx;
          s.tyAtDrag   = s.ty;
        } else {
          // scale===1: track for swipe-to-navigate
          s.swipeTracking = true;
          s.dragging      = false;
          s.swipeStartX   = touches[0].clientX;
          s.swipeStartY   = touches[0].clientY;
        }
      }
    };

    const onTouchMove = (e) => {
      const s = stateRef.current;
      const touches = e.touches;

      if (s.pinching && touches.length === 2) {
        e.preventDefault();
        const dist     = getTouchDist(touches[0], touches[1]);
        // Ratio of current distance to initial pinch distance
        const pinchRatio = dist / s.pinchDist0;
        // New scale = what scale was when pinch started * ratio, hard-clamped
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s.scaleAtPinch * pinchRatio));

        // Stable midpoint-based zoom:
        // We want the world point under the pinch midpoint to remain fixed.
        // Screen center is our transform-origin (center center).
        // The pinch mid in screen space: s.pinchMidX, s.pinchMidY
        // Screen center:
        const cx = window.innerWidth  / 2;
        const cy = window.innerHeight / 2;
        // Vector from screen center to pinch midpoint
        const midOffX = s.pinchMidX - cx;
        const midOffY = s.pinchMidY - cy;

        // At scaleAtPinch the image is positioned at txAtPinch/tyAtPinch.
        // The point in image-local space under the pinch mid:
        //   localX = (midOffX - txAtPinch) / scaleAtPinch
        // After applying newScale, to keep that point under the pinch mid:
        //   midOffX = localX * newScale + newTx
        // => newTx = midOffX - localX * newScale
        const localX = (midOffX - s.txAtPinch) / s.scaleAtPinch;
        const localY = (midOffY - s.tyAtPinch) / s.scaleAtPinch;
        const rawTx = midOffX - localX * newScale;
        const rawTy = midOffY - localY * newScale;

        const { tx, ty } = clampPan(newScale, rawTx, rawTy);
        s.scale = newScale;
        s.tx    = tx;
        s.ty    = ty;
        applyTransform(newScale, tx, ty, false);

      } else if (s.dragging && touches.length === 1) {
        e.preventDefault();
        const dx = touches[0].clientX - s.dragStartX;
        const dy = touches[0].clientY - s.dragStartY;
        const { tx, ty } = clampPan(s.scale, s.txAtDrag + dx, s.tyAtDrag + dy);
        s.tx = tx;
        s.ty = ty;
        applyTransform(s.scale, tx, ty, false);
      }
    };

    const onTouchEnd = (e) => {
      const s = stateRef.current;

      // Swipe-to-navigate: only fires at scale===1 on full finger lift
      if (s.swipeTracking && e.touches.length === 0 && s.scale <= 1) {
        const dx = (e.changedTouches[0]?.clientX ?? 0) - s.swipeStartX;
        const dy = (e.changedTouches[0]?.clientY ?? 0) - s.swipeStartY;
        const SWIPE_MIN_X  = 60;   // px horizontal threshold
        const SWIPE_MAX_Y  = 80;   // px max vertical drift allowed
        if (Math.abs(dx) > SWIPE_MIN_X && Math.abs(dy) < SWIPE_MAX_Y) {
          if (dx < 0) {
            navCallbackRef.current.next?.();
          } else {
            navCallbackRef.current.prev?.();
          }
        }
        s.swipeTracking = false;
        return;
      }
      s.swipeTracking = false;

      if (e.touches.length < 2) {
        s.pinching = false;
        if (e.touches.length === 1) {
          s.dragging   = s.scale > 1;
          s.dragStartX = e.touches[0].clientX;
          s.dragStartY = e.touches[0].clientY;
          s.txAtDrag   = s.tx;
          s.tyAtDrag   = s.ty;
        }
      }
      if (e.touches.length === 0) {
        s.dragging = false;
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, [applyTransform, clampPan, resetZoom]);

  // ── Update cursor style when image changes ──
  useEffect(() => {
    const el = imgRef.current;
    if (el) el.style.cursor = 'default';
  }, [item]);

  // ── Backdrop click: only close if not zoomed ──
  const handleBackdropClick = useCallback(() => {
    const s = stateRef.current;
    if (s.scale > 1) {
      resetZoom(true);
    } else {
      onClose();
    }
  }, [onClose, resetZoom]);

  // ── Nav: reset zoom before navigating ──
  const handlePrev = useCallback(() => { resetZoom(false); onPrev(); }, [resetZoom, onPrev]);
  const handleNext = useCallback(() => { resetZoom(false); onNext(); }, [resetZoom, onNext]);

  // Keep navCallbackRef current so the touch handler can call these without stale closure
  navCallbackRef.current.prev = handlePrev;
  navCallbackRef.current.next = handleNext;

  return createPortal(
    <motion.div
      ref={backdropRef}
      className="mg-lightbox-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleBackdropClick}
      style={{ zIndex: 2147483647 }}
    >
      {/* Nav buttons live on the backdrop — completely outside the zooming frame.
          They never scale with the image. On mobile they hide when zoomed (setNavVisibility). */}
      <button
        ref={navPrevRef}
        className="mg-lb-nav mg-lb-prev"
        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
        aria-label="Previous image"
        style={{ transition: 'opacity 0.25s ease' }}
      >‹</button>
      <button
        ref={navNextRef}
        className="mg-lb-nav mg-lb-next"
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        aria-label="Next image"
        style={{ transition: 'opacity 0.25s ease' }}
      >›</button>

      {/*
        Framer handles the entrance pop animation on this wrapper.
        imgRef targets the inner mg-lightbox-content div so our direct DOM
        transform writes (pinch/pan/zoom) never conflict with framer's state.
        The ENTIRE content box — border-radius, box-shadow, image — zooms as one unit.
      */}
      <motion.div
        initial={{ scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.88, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onClick={e => e.stopPropagation()}
      >
        <div
          ref={imgRef}
          className="mg-lightbox-content"
          style={{ transformOrigin: 'center center', willChange: 'transform' }}
        >
          <button className="mg-lb-close" onClick={onClose} aria-label="Close lightbox">✕</button>

          <img
            ref={rawImgRef}
            src={item.img}
            alt={`Media ${item.id}`}
            className="mg-lb-img"
            draggable={false}
            style={{
              display: 'block',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              touchAction: 'none',
            }}
          />
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// ─────────────────────────────────────────────
// MASONRY GRID
// ─────────────────────────────────────────────
const MasonryGrid = ({ items, onImageClick }) => {
  const columns = useMedia(
    ['(min-width:1200px)', '(min-width:900px)', '(min-width:600px)', '(min-width:360px)'],
    [3, 2, 2, 2],
    2
  );
  const [containerRef, { width }] = useMeasure();
  const [imagesReady, setImagesReady] = useState(false);
  const hasMounted = useRef(false);

  useEffect(() => {
    Promise.all(
      items.map(
        src =>
          new Promise(resolve => {
            const img = new Image();
            img.src = src.img;
            img.onload = img.onerror = resolve;
          })
      )
    ).then(() => setImagesReady(true));
  }, [items]);

  const grid = useMemo(() => {
    if (!width) return [];
    const colHeights = new Array(columns).fill(0);
    const columnWidth = width / columns;
    return items.map(child => {
      const col = colHeights.indexOf(Math.min(...colHeights));
      const x = col * columnWidth;
      const y = colHeights[col];
      const aspectH = child.height * (columnWidth / 400);
      colHeights[col] += aspectH;
      return { ...child, x, y, w: columnWidth, h: aspectH };
    });
  }, [columns, items, width]);

  const totalHeight = useMemo(() => {
    if (!grid.length) return 0;
    return Math.max(...grid.map(i => i.y + i.h));
  }, [grid]);

  useLayoutEffect(() => {
    if (!imagesReady) return;
    grid.forEach((item, index) => {
      const el = document.querySelector(`[data-mgkey="${item.id}"]`);
      if (!el) return;
      if (!hasMounted.current) {
        gsap.fromTo(
          el,
          { opacity: 0, y: item.y + 40, x: item.x, width: item.w, height: item.h, filter: 'blur(8px)' },
          {
            opacity: 1,
            y: item.y,
            x: item.x,
            width: item.w,
            height: item.h,
            filter: 'blur(0px)',
            duration: 0.75,
            ease: 'power3.out',
            delay: index * 0.045,
          }
        );
      } else {
        gsap.to(el, { x: item.x, y: item.y, width: item.w, height: item.h, duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
      }
    });
    hasMounted.current = true;
  }, [grid, imagesReady]);

  const handleMouseEnter = (id) => {
    gsap.to(`[data-mgkey="${id}"]`, { scale: 0.96, duration: 0.28, ease: 'power2.out' });
  };
  const handleMouseLeave = (id) => {
    gsap.to(`[data-mgkey="${id}"]`, { scale: 1, duration: 0.28, ease: 'power2.out' });
  };

  return (
    <div ref={containerRef} className="mg-masonry-list" style={{ height: totalHeight || 'auto' }}>
      {grid.map(item => (
        <div
          key={item.id}
          data-mgkey={item.id}
          className="mg-masonry-item"
          style={{ position: 'absolute', top: 0, left: 0, willChange: 'transform, opacity' }}
          onClick={() => onImageClick(item)}
          onMouseEnter={() => handleMouseEnter(item.id)}
          onMouseLeave={() => handleMouseLeave(item.id)}
        >
          <div className="mg-masonry-img-wrap">
            <img
              src={item.img}
              alt={`Gallery ${item.id}`}
              className="mg-masonry-img"
              loading="lazy"
              draggable={false}
            />
            <div className="mg-masonry-overlay">
              <span className="mg-masonry-zoom">⊕</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// ANIMATED MEDIA LINK ITEM
// ─────────────────────────────────────────────
const MediaLinkItem = ({ item, index, isSelected, onSelect }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: false });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 32 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 32 }}
      transition={{ duration: 0.38, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`mg-link-item ${isSelected ? 'mg-link-item--selected' : ''}`}
      onClick={() => {
        onSelect(index);
        window.open(item.url, '_blank', 'noopener,noreferrer');
      }}
      role="link"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && window.open(item.url, '_blank', 'noopener,noreferrer')}
      aria-label={`Open ${item.title}`}
    >
      <div className="mg-link-play-btn" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M8 5v14l11-7z"/>
        </svg>
      </div>

      <div className="mg-link-body">
        <h3 className="mg-link-title">{item.title}</h3>
        <p className="mg-link-desc">{item.description}</p>
        <span className="mg-link-channel">
          <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" aria-hidden="true">
            <path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 1.99-.14 3.47-.44 4.47-.27.87-.87 1.47-1.74 1.74-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 18.5c-3.75 0-6.07-.14-7.36-.44-.87-.27-1.47-.87-1.74-1.74-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-1.99.14-3.47.44-4.47.27-.87.87-1.47 1.74-1.74.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5.5c3.75 0 6.07.14 7.36.44.87.27 1.47.87 1.74 1.74z"/>
          </svg>
          {item.channel}
        </span>
      </div>

      <div className="mg-link-arrow" aria-hidden="true">›</div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const MediaGallery = () => {
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [selectedLink, setSelectedLink] = useState(-1);
  const heroRef = useRef(null);

  // Hero entrance
  useEffect(() => {
    if (!heroRef.current) return;
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: -24 },
      { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out', delay: 0.1 }
    );
  }, []);

  const handleImageClick = useCallback((item) => {
    const idx = GALLERY_ITEMS.findIndex(g => g.id === item.id);
    setLightboxIndex(idx);
    setLightboxItem(GALLERY_ITEMS[idx]);
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightboxItem(null);
    setLightboxIndex(null);
  }, []);

  const handlePrev = useCallback(() => {
    const newIdx = (lightboxIndex - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length;
    setLightboxIndex(newIdx);
    setLightboxItem(GALLERY_ITEMS[newIdx]);
  }, [lightboxIndex]);

  const handleNext = useCallback(() => {
    const newIdx = (lightboxIndex + 1) % GALLERY_ITEMS.length;
    setLightboxIndex(newIdx);
    setLightboxItem(GALLERY_ITEMS[newIdx]);
  }, [lightboxIndex]);

  return (
    <section className="mg-section">
      {/* ── Page Hero ── */}
      <div ref={heroRef} className="mg-hero" style={{ opacity: 0 }}>
        <h1 className="mg-hero-title">
          Media <span className="mg-hero-title-accent">Gallery</span>
        </h1>
        <p className="mg-hero-sub">
          Explore our robotics in action — from farm fields to factory floors.
        </p>
      </div>

      {/* ── Two-Column Layout ── */}
      <div className="mg-layout">

        {/* LEFT — Masonry Image Gallery */}
        <div className="mg-col-gallery">
          <div className="mg-col-header">
            <span className="mg-col-icon">🖼</span>
            <div>
              <h2 className="mg-col-title">Photo Gallery</h2>
              <p className="mg-col-subtitle">Click any image to view full size</p>
            </div>
          </div>
          <MasonryGrid items={GALLERY_ITEMS} onImageClick={handleImageClick} />
        </div>

        {/* RIGHT — Animated Media Links */}
        <div className="mg-col-links">
          <div className="mg-col-header">
            <span className="mg-col-icon">▶</span>
            <div>
              <h2 className="mg-col-title">Video Showcase</h2>
              <p className="mg-col-subtitle">Watch our robots in the real world</p>
            </div>
          </div>

          <div className="mg-links-list">
            {MEDIA_LINKS.map((item, index) => (
              <MediaLinkItem
                key={item.id}
                item={item}
                index={index}
                isSelected={selectedLink === index}
                onSelect={setSelectedLink}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Lightbox (portalled to document.body) ── */}
      {lightboxItem && (
        <Lightbox
          item={lightboxItem}
          onClose={handleLightboxClose}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </section>
  );
};

export default MediaGallery;